import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  collection,
  onSnapshot,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthProvider';
import { useActiveSession } from '../ActiveSessionContext';
import PageWrapper from '../components/PageWrapper';
import { useUI } from '../UIContext';
import { useNavigate, useLocation } from 'react-router-dom';
import BorderedBox from '../components/BorderedBox';
import TuneCardBox from '../components/TuneCardBox';
import StyledCheckbox from '../components/StyledCheckbox';
import myIcon from '../assets/logo512.png'; // adjust path
import { normalizeGenre, normalizeType, keyRoot, normalizeTag, normalizeTagsArray } from '../utils/normalize';
import CustomTagInput from '../components/CustomTagInput';
import { useUserPrefs } from '../UserPrefsProvider';
import { useOnboarding } from '../onboarding';
import TagFilterControl from '../components/TagFilterControl';
import dexie from '../offlineDB';
import CreateIrishSetForm from '../components/CreateIrishSetForm'; // adjust path if needed
import { getUserProfile, sendBatchToUser, sendSetToUser } from '../utils/socialFirestore';
import SessionPromptModal from '../components/SessionPromptModal';
import ActionConfirmModal from '../components/ActionConfirmModal';
import { getModeLabelsForEntity } from '../utils/modeLabels';
import ModeSwitcher from '../components/ModeSwitcher';
import { FaQuestionCircle } from 'react-icons/fa';
import AppSelect from '../components/AppSelect';
import TimeFilterPanel from '../components/TimeFilterPanel';
import UndoBanner from '../components/UndoBanner';
import PreviewModePill from '../components/PreviewModePill';
import SwipeablePinnableRow from '../components/SwipeablePinnableRow';
import SendExportModal from '../components/SendExportModal';
import ExportModal from '../components/ExportModal';

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SETS_FILTERS_STORAGE_KEY = 'setsFiltersByMode';
const LEGACY_SETS_FILTERS_STORAGE_KEY = 'setsFilters';

const normalizeModeKey = (mode) => (mode === 'notes' || mode === 'all' ? mode : 'tunes');

const hasFilterValues = (filters) => Boolean(filters && typeof filters === 'object' && Object.keys(filters).length > 0);

const getFiltersForMode = (byMode, mode, lastPureMode) => {
  const targetMode = normalizeModeKey(mode);
  const direct = byMode?.[targetMode];
  if (hasFilterValues(direct)) return direct;
  if (targetMode === 'all') {
    const fallbackMode = normalizeModeKey(lastPureMode || 'tunes');
    const fallback = byMode?.[fallbackMode];
    if (hasFilterValues(fallback)) return fallback;
  }
  return {};
};

const readLocalSetFilterBundle = () => {
  let byMode = {};
  let lastPureMode = 'tunes';
  let legacy = {};

  try {
    const raw = JSON.parse(localStorage.getItem(SETS_FILTERS_STORAGE_KEY) || '{}');
    if (raw && typeof raw === 'object') {
      byMode = raw.byMode && typeof raw.byMode === 'object' ? raw.byMode : {};
      lastPureMode = normalizeModeKey(raw.lastPureMode || 'tunes');
    }
  } catch {}

  try {
    legacy = JSON.parse(localStorage.getItem(LEGACY_SETS_FILTERS_STORAGE_KEY) || '{}');
  } catch {}

  return { byMode, lastPureMode, legacy };
};

const toDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const d = value.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const getSetTimeFieldDate = (set, basis) => {
  if (basis === 'viewed') return toDateValue(set.lastViewedAt);
  if (basis === 'created') return toDateValue(set.createdAt);
  return toDateValue(set.updatedAt) || toDateValue(set.createdAt);
};

const formatShortUsDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

const matchesSetTimeRange = (set, range, basis, selectedMonth, selectedYear) => {
  if (!range) return true;
  const date = getSetTimeFieldDate(set, basis);
  if (!date) return false;

  const now = new Date();
  if (range === 'past-week') {
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    return date >= start && date <= now;
  }
  if (range === 'this-month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return date >= start && date <= now;
  }
  if (range === 'past-month') {
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return date >= start && date <= now;
  }
  if (range === 'past-year') {
    const start = new Date(now);
    start.setDate(start.getDate() - 365);
    return date >= start && date <= now;
  }
  if (range === 'year') {
    const year = Number(selectedYear);
    if (!year) return true;
    return date.getFullYear() === year;
  }
  if (range === 'month') {
    if (!selectedMonth) return true;
    const [y, m] = String(selectedMonth).split('-').map(Number);
    if (!y || !m) return true;
    return date.getFullYear() === y && (date.getMonth() + 1) === m;
  }
  return true;
};

export default function SetsPage() {
  const resultsContainerRef = useRef(null);
  const [scrollAreaHeight, setScrollAreaHeight] = useState(320);
  const updateScrollHeight = React.useCallback(() => {
    if (!resultsContainerRef.current) return;
    const rect = resultsContainerRef.current.getBoundingClientRect();
    setScrollAreaHeight(window.innerHeight - rect.top - 8);
  }, []);
  useEffect(() => {
    updateScrollHeight();
    window.addEventListener('resize', updateScrollHeight);
    return () => window.removeEventListener('resize', updateScrollHeight);
  }, [updateScrollHeight]);
  const { user } = useAuth();
  const { activeSession } = useActiveSession();
  const { inputStyle, textStyle } = useUI();
  const [sets, setSets] = useState([]);
  const [tunes, setTunes] = useState([]);

  const [selectedSetIds, setSelectedSetIds] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [undoTimer, setUndoTimer] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [filterSentBy, setFilterSentBy] = useState('');
  const [swipeSendTarget, setSwipeSendTarget] = useState(null); // set being sent via swipe
  const [showBulkSendPicker, setShowBulkSendPicker] = useState(false);
  const [showBulkExportModal, setShowBulkExportModal] = useState(false);

  // New filter state
  const [filterSetQuery, setFilterSetQuery] = useState('');
  const [filterSetRating, setFilterSetRating] = useState('');
  const [filterSetLabel, setFilterSetLabel] = useState('');
  const [labelOptions, setLabelOptions] = useState([]);
  const [filterSetGenre, setFilterSetGenre] = useState('');
const [filterSetType, setFilterSetType] = useState('');
const [selectedSetTags, setSelectedSetTags] = useState([]);
const [setTagLogic, setSetTagLogic] = useState('AND');
const { prefs, labels, modeGenres, modeTypes } = useUserPrefs();
const { isSimpleMode, unlockFullMode, NudgeBanner } = useOnboarding();
const currentMode = prefs?.appMode || 'tunes';
const [showSetTagFilters, setShowSetTagFilters] = useState(false);
const [filterSetTimeRange, setFilterSetTimeRange] = useState('');
const [filterSetTimeBasis, setFilterSetTimeBasis] = useState('updated');
const [filterSetTimeMonth, setFilterSetTimeMonth] = useState('');
const [filterSetTimeYear, setFilterSetTimeYear] = useState('');
const [showSetTimeFilters, setShowSetTimeFilters] = useState(false);
const [setMonthPickerYear, setSetMonthPickerYear] = useState(new Date().getFullYear());
const [sortOrder, setSortOrder] = useState('newest');
const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
const [showAdvancedLockPopup, setShowAdvancedLockPopup] = useState(false);
const [bulkActionValue, setBulkActionValue] = useState('');
const [showBulkTagInput, setShowBulkTagInput] = useState(false);
const [bulkTags, setBulkTags] = useState([]);
const [showBulkAddToSetPicker, setShowBulkAddToSetPicker] = useState(false);
const [bulkAddToSetQuery, setBulkAddToSetQuery] = useState('');
const [showIrishForm, setShowIrishForm] = useState(false);
const [viewFilter, setViewFilter] = useState('all'); // 'all' | 'sets' | 'programs'
const [useViewFilterDropdown, setUseViewFilterDropdown] = useState(() => (
  typeof window !== 'undefined' ? window.innerWidth <= 700 : false
));
const [notice, setNotice] = useState(null);
const [pendingSessionSet, setPendingSessionSet] = useState(null);
const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
const [showCreateSetFromSelectedModal, setShowCreateSetFromSelectedModal] = useState(false);
const showNotice = (text, tone = 'info') => setNotice({ text, tone });
const previousModeRef = useRef(currentMode);

const buildSetSequenceState = () => ({
  setTunes: sortedSets.map((s) => ({ id: s.id, title: (s.title || '').trim() || 'Untitled', type: 'set' })),
});

useEffect(() => {
  if (!notice) return undefined;
  const timer = setTimeout(() => setNotice(null), 2600);
  return () => clearTimeout(timer);
}, [notice]);

useEffect(() => {
  if (previousModeRef.current === currentMode) return;
  previousModeRef.current = currentMode;
  setSelectedSetIds([]);
  setBulkActionValue('');
  setShowBulkTagInput(false);
  setBulkTags([]);
  setShowBulkAddToSetPicker(false);
  setBulkAddToSetQuery('');
  setShowBulkSendPicker(false);
  setShowBulkExportModal(false);
  setShowDeleteSelectedModal(false);
  setShowCreateSetFromSelectedModal(false);
}, [currentMode]);

useEffect(() => {
  const onResize = () => setUseViewFilterDropdown(window.innerWidth <= 700);
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
}, []);


// Sync sentBy filter from URL params
useEffect(() => {
  const params = new URLSearchParams(location.search);
  const sb = params.get('sentBy') || '';
  if (sb) setFilterSentBy(sb);
}, [location.search]);

useEffect(() => {
  if (!user) return;

  if (!navigator.onLine) {
    dexie.tunes.toArray().then(localTunes => {
      setTunes(localTunes);
    }).catch(err => {
      console.warn('Dexie tune load failed:', err);
    });
    return;
  }

  const tunesRef = collection(db, 'users', user.uid, 'tunes');
  const unsubscribe = onSnapshot(tunesRef, snapshot => {
    const all = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setTunes(all);
    dexie.tunes.bulkPut(all).catch(err => {
      console.warn('Dexie tune cache failed:', err);
    });
  });

  return () => unsubscribe();
}, [user]);

useEffect(() => {
  if (!user) return;

  const loadFilters = async () => {
    const apply = (f = {}) => {
      setFilterSetQuery(f.filterSetQuery || '');
      setFilterSetRating(f.filterSetRating || '');
      setFilterSetGenre(f.filterSetGenre !== undefined ? f.filterSetGenre : (labels?.hideKeyFilter ? labels?.defaultGenre : '') || '');
      setFilterSetType(f.filterSetType || '');
      setSelectedSetTags(Array.isArray(f.selectedSetTags) ? normalizeTagsArray(f.selectedSetTags) : []);
      setSetTagLogic(f.setTagLogic || 'AND');
      setFilterSetTimeRange(f.filterSetTimeRange || '');
      setFilterSetTimeBasis(f.filterSetTimeBasis || 'updated');
      setFilterSetTimeMonth(f.filterSetTimeMonth || '');
      setFilterSetTimeYear(f.filterSetTimeYear || '');
      setSortOrder(f.sortOrder || 'newest');
    };

    const localBundle = readLocalSetFilterBundle();

    if (!navigator.onLine) {
      const localSaved = getFiltersForMode(localBundle.byMode, currentMode, localBundle.lastPureMode);
      apply(hasFilterValues(localSaved) ? localSaved : localBundle.legacy || {});
      return;
    }

    const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : {};
    const byMode = (data.setsFiltersByMode && typeof data.setsFiltersByMode === 'object')
      ? data.setsFiltersByMode
      : localBundle.byMode;
    const lastPureMode = normalizeModeKey(data.setsFiltersLastPureMode || localBundle.lastPureMode || 'tunes');
    const saved = getFiltersForMode(byMode, currentMode, lastPureMode);
    const fallbackLegacy = data.setsFilters || localBundle.legacy || {};
    apply(hasFilterValues(saved) ? saved : fallbackLegacy);
  };

  loadFilters();
}, [user, currentMode, labels?.hideKeyFilter, labels?.defaultGenre]);

useEffect(() => {
  const saveFilters = () => {
    const localBundle = readLocalSetFilterBundle();
    const filtersToSave = {
      filterSetQuery,
      filterSetRating,
      filterSetGenre,
      filterSetType,
      selectedSetTags,
      setTagLogic,
      filterSetTimeRange,
      filterSetTimeBasis,
      filterSetTimeMonth,
      filterSetTimeYear,
      sortOrder,
    };
    const nextByMode = {
      ...(localBundle.byMode || {}),
      [normalizeModeKey(currentMode)]: filtersToSave
    };
    const nextLastPureMode = currentMode === 'all'
      ? normalizeModeKey(localBundle.lastPureMode || 'tunes')
      : normalizeModeKey(currentMode);
    localStorage.setItem(
      SETS_FILTERS_STORAGE_KEY,
      JSON.stringify({ byMode: nextByMode, lastPureMode: nextLastPureMode })
    );
    localStorage.setItem(LEGACY_SETS_FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));
  };

  window.addEventListener('beforeunload', saveFilters);
  return () => window.removeEventListener('beforeunload', saveFilters);
}, [
  filterSetQuery,
  filterSetRating,
  filterSetGenre,
  filterSetType,
  selectedSetTags,
  setTagLogic,
  filterSetTimeRange,
  filterSetTimeBasis,
  filterSetTimeMonth,
  filterSetTimeYear,
  sortOrder,
  currentMode
]);



  // Fetch label options
  useEffect(() => {
    const fetchLabels = async () => {
      if (!user) return;
      const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        const custom = Array.isArray(data.customRatingLabels) ? data.customRatingLabels : ['Awesome', 'Meh', 'To edit'];
        setLabelOptions(custom.filter(l => l !== '—'));
      } else {
        setLabelOptions(['Awesome', 'Meh', 'To edit']);
      }
    };
    fetchLabels();
  }, [user]);

useEffect(() => {
  if (!user) return;

  if (!navigator.onLine) {
    dexie.sets.toArray().then(localSets => {
      setSets(localSets);
    }).catch(err => {
      console.warn('Dexie set load failed:', err);
    });
    return;
  }

  const setsRef = collection(db, 'users', user.uid, 'sets');
  const tunesRef = collection(db, 'users', user.uid, 'tunes');

  const unsubscribe = onSnapshot(setsRef, async snapshot => {
    const rawSets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const tuneIds = new Set();
    rawSets.forEach(set => {
      (set.tunes || []).forEach(t => {
        if (t?.id) tuneIds.add(t.id);
      });
    });

    const allTuneDocs = await getDocs(tunesRef);
    const tuneMap = {};
    allTuneDocs.forEach(doc => {
      tuneMap[doc.id] = { id: doc.id, ...doc.data() };
    });

    const hydratedSets = rawSets.map(set => {
      const hydratedTunes = (set.tunes || []).map(t => {
        if (t?.type === 'set') {
          const fullSet = rawSets.find((candidate) => candidate.id === t.id);
          return fullSet
            ? {
                id: fullSet.id,
                title: fullSet.title || t.title || 'Untitled Set',
                type: 'set',
                mode: fullSet.mode || 'tunes',
              }
            : { ...t, type: 'set', missing: true };
        }
        const full = tuneMap[t.id];
        return full ? { ...full } : { ...t, missing: true };
      });
      return {
        ...set,
        tunes: hydratedTunes
      };
    });

    setSets(hydratedSets);
    dexie.sets.bulkPut(hydratedSets).catch(err =>
      console.warn('Dexie set caching failed:', err)
    );
  });

  return () => unsubscribe();
}, [user]);


useEffect(() => {
  if (!user) return;

  const localBundle = readLocalSetFilterBundle();
  const filtersToSave = {
    filterSetQuery,
    filterSetRating,
    filterSetGenre,
    filterSetType,
    selectedSetTags,
    setTagLogic,
    filterSetTimeRange,
    filterSetTimeBasis,
    filterSetTimeMonth,
    filterSetTimeYear,
    sortOrder,
  };

  const nextByMode = {
    ...(localBundle.byMode || {}),
    [normalizeModeKey(currentMode)]: filtersToSave
  };
  const nextLastPureMode = currentMode === 'all'
    ? normalizeModeKey(localBundle.lastPureMode || 'tunes')
    : normalizeModeKey(currentMode);
  localStorage.setItem(
    SETS_FILTERS_STORAGE_KEY,
    JSON.stringify({ byMode: nextByMode, lastPureMode: nextLastPureMode })
  );
  localStorage.setItem(LEGACY_SETS_FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));

  const ref = doc(db, 'users', user.uid, 'preferences', 'settings');
  setDoc(
    ref,
    {
      setsFiltersByMode: nextByMode,
      setsFiltersLastPureMode: nextLastPureMode,
      setsFilters: filtersToSave
    },
    { merge: true }
  );
}, [
  user,
  filterSetQuery,
  filterSetRating,
  filterSetGenre,
  filterSetType,
  selectedSetTags,
  setTagLogic,
  filterSetTimeRange,
  filterSetTimeBasis,
  filterSetTimeMonth,
  filterSetTimeYear,
  sortOrder,
  currentMode
]);


  const toggleSetSelection = (id) => {
    setSelectedSetIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (allVisibleSetsSelected) {
      setSelectedSetIds((prev) => prev.filter((id) => !visibleSetIds.includes(id)));
    } else {
      setSelectedSetIds((prev) => Array.from(new Set([...prev, ...visibleSetIds])));
    }
  };

  // Remove deleted setId from each member tune's sets[] array (Firestore + Dexie)
  const cleanupTuneSetRefs = async (setId, setTunes) => {
    const tuneIds = (setTunes || [])
      .map(t => (typeof t === 'string' ? t : t?.id))
      .filter(Boolean);
    for (const tuneId of tuneIds) {
      try {
        if (navigator.onLine) {
          const tuneRef = doc(db, 'users', user.uid, 'tunes', tuneId);
          const tuneSnap = await getDoc(tuneRef);
          if (tuneSnap.exists()) {
            const cleaned = (tuneSnap.data().sets || []).filter(s => s.setId !== setId);
            await updateDoc(tuneRef, { sets: cleaned });
          }
        }
        const localTune = await dexie.tunes.get(tuneId);
        if (localTune) {
          const cleaned = (localTune.sets || []).filter(s => s.setId !== setId);
          await dexie.tunes.put({ ...localTune, sets: cleaned });
        }
      } catch (err) {
        console.warn('[cleanupTuneSetRefs] could not clean tune', tuneId, err);
      }
    }
  };

  const deleteSet = async (id) => {
  let data = null;

  if (navigator.onLine) {
    const ref = doc(db, 'users', user.uid, 'sets', id);
    const snap = await getDoc(ref);
    data = snap.exists() ? snap.data() : null;
    await deleteDoc(ref);
  } else {
    data = await dexie.sets.get(id);
    await dexie.pendingWrites.put({
      id: `${id}__delete`,
      type: 'delete',
      collection: 'sets',
      docId: id,
      data: {}
    });
  }
  await dexie.sets.delete(id); // always remove from local cache
  if (data) await cleanupTuneSetRefs(id, data.tunes);

  return { id, data };
};

const tagSetToActiveSession = async (targetSetId) => {
  if (!user || !activeSession || !targetSetId) return 0;
  const targetSet = sets.find((s) => s.id === targetSetId);
  if (!targetSet || targetSet.isProgram) return 0;
  const existingSessions = Array.isArray(targetSet.sessions) ? targetSet.sessions : [];
  if (existingSessions.some((s) => s.sessionId === activeSession.id)) return 0;

  const nextSessions = [
    ...existingSessions,
    {
      sessionId: activeSession.id,
      location: activeSession.location || '',
      startedAt: activeSession.startedAt || new Date().toISOString(),
    },
  ];

  if (navigator.onLine) {
    await updateDoc(doc(db, 'users', user.uid, 'sets', targetSetId), { sessions: nextSessions });
  }

  await dexie.sets.put({ ...targetSet, id: targetSetId, sessions: nextSessions });
  if (!navigator.onLine) {
    await dexie.pendingWrites.put({
      id: `${targetSetId}__sessionTag`,
      type: 'update',
      collection: 'sets',
      docId: targetSetId,
      data: { ...targetSet, id: targetSetId, sessions: nextSessions }
    });
  }

  return 1;
};

  const deleteSelectedSets = async () => {
  if (!user || selectedSetIds.length === 0) return;

  const setsToDelete = [];

  for (const setId of selectedSetIds) {
    let data = null;

    if (navigator.onLine) {
      const ref = doc(db, 'users', user.uid, 'sets', setId);
      const snap = await getDoc(ref);
      data = snap.exists() ? snap.data() : null;
      await deleteDoc(ref);
    } else {
      data = await dexie.sets.get(setId);
      await dexie.pendingWrites.put({
        id: `${setId}__delete`,
        type: 'delete',
        collection: 'sets',
        docId: setId,
        data: {}
      });
    }
    await dexie.sets.delete(setId); // always remove from local cache

    if (data) {
      setsToDelete.push({ id: setId, data });
    }
  }

  setPendingDelete(setsToDelete);
  setSelectedSetIds([]);

  const timer = setTimeout(async () => {
    setPendingDelete(null);
    setUndoTimer(null);
    // Undo window passed — clean up stale tune.sets references
    for (const { id, data } of setsToDelete) {
      await cleanupTuneSetRefs(id, data?.tunes);
    }
  }, 5000);

  setUndoTimer(timer);
};

const undoDelete = async () => {
  if (!user || !pendingDelete) return;
  clearTimeout(undoTimer);

for (const { id, data } of pendingDelete) {
  const ref = doc(db, 'users', user.uid, 'sets', id);

  await dexie.sets.put({ ...data, id }); // Always update Dexie

  await dexie.pendingWrites.put({
    id: `${id}__undo`,
    type: 'add',
    collection: 'sets',
    docId: id,
    data: { ...data, id }
  });

  if (navigator.onLine) {
    try {
      await setDoc(ref, data);
      await dexie.pendingWrites.delete(`${id}__undo`);
    } catch (err) {
      console.warn(`Failed to restore set ${id} online; will retry`, err);
    }
  }
}


  setPendingDelete(null);
  setUndoTimer(null);
};

const swipeDeleteSet = async (set) => {
  if (!user) return;
  const data = { ...set };
  const id = set.id;

  if (navigator.onLine) {
    await deleteDoc(doc(db, 'users', user.uid, 'sets', id));
  } else {
    await dexie.pendingWrites.put({ id: `${id}__delete`, type: 'delete', collection: 'sets', docId: id, data: {} });
  }
  await dexie.sets.delete(id);

  setPendingDelete([{ id, data }]);
  const timer = setTimeout(async () => {
    setPendingDelete(null);
    setUndoTimer(null);
    // Undo window passed — clean up stale tune.sets references
    await cleanupTuneSetRefs(id, data?.tunes);
  }, 5000);
  setUndoTimer(timer);
};

const swipePinSet = async (set) => {
  if (!user) return;
  const newPinned = !set.pinned;
  try {
    if (navigator.onLine) {
      await updateDoc(doc(db, 'users', user.uid, 'sets', set.id), { pinned: newPinned });
    } else {
      await dexie.sets.update(set.id, { pinned: newPinned });
      await dexie.pendingWrites.put({ id: `${set.id}__pin`, type: 'update', collection: 'sets', docId: set.id, data: { pinned: newPinned } });
    }
    setSets(prev => prev.map(s => s.id === set.id ? { ...s, pinned: newPinned } : s));
  } catch (err) {
    console.error('Failed to pin set:', err);
  }
};

const handleSwipeSendSet = async (recipientUid, contact) => {
  if (!user) return;
  const myProfile = await getUserProfile(user.uid);
  const tuneIds = (swipeSendTarget.tunes || [])
    .filter(t => t.type !== 'set')
    .map(t => t.id)
    .filter(Boolean);
  await sendSetToUser(user.uid, myProfile, recipientUid, swipeSendTarget.id, swipeSendTarget.title, tuneIds);
  if (navigator.onLine) {
    const setRef = doc(db, 'users', user.uid, 'sets', swipeSendTarget.id);
    const setSnap = await getDoc(setRef);
    if (setSnap.exists()) {
      const existing = setSnap.data().sharedWith || [];
      if (!existing.some(s => s.uid === recipientUid)) {
        await updateDoc(setRef, {
          sharedWith: [...existing, {
            uid: recipientUid,
            displayName: contact?.displayName || '',
            username: contact?.username || '',
            sentAt: new Date().toISOString()
          }],
          updatedAt: new Date().toISOString()
        });
      }
    }
  }
};

const getCollectionBadge = (entityMode) => (
  (getModeLabelsForEntity(currentMode, entityMode || 'tunes')?.Collection || 'Set').toUpperCase()
 );
const selectedSetDeleteLabel = (() => {
  const selected = sets.filter((s) => selectedSetIds.includes(s.id));
  const modeSet = new Set(selected.map((s) => s.mode || 'tunes'));
  if (modeSet.size > 1) return { singular: 'collection', plural: 'collections' };
  if (modeSet.has('notes')) return { singular: 'folder', plural: 'folders' };
  return { singular: 'set', plural: 'sets' };
})();
const deletedSetUndoLabel = (() => {
  const removed = Array.isArray(pendingDelete) ? pendingDelete : [];
  const modeSet = new Set(removed.map((s) => s?.data?.mode || s?.mode || 'tunes'));
  if (modeSet.size > 1) return { singular: 'collection', plural: 'collections' };
  if (modeSet.has('notes')) return { singular: 'folder', plural: 'folders' };
  return { singular: 'set', plural: 'sets' };
})();
const modeFilteredForViewFilter = sets.filter(s => currentMode === 'all' || (s.mode || 'tunes') === currentMode);
 const allCount = modeFilteredForViewFilter.length;
 const programCount = modeFilteredForViewFilter.filter(s => s.isProgram).length;
 const programSetCount = modeFilteredForViewFilter.filter(s => !s.isProgram && s.fromProgram).length;
 const setCount = allCount - programCount - programSetCount;
 const viewFilterOptions = [
  { key: 'all', label: `All (${allCount})` },
  { key: 'sets', label: `Sets (${setCount})` },
  ...(!isSimpleMode ? [
    { key: 'programs', label: `Program Templates (${programCount})` },
    { key: 'program-sets', label: `Program Sets (${programSetCount})` },
  ] : []),
 ];
 const shouldShowViewFilter = !isSimpleMode && currentMode !== 'notes' && (programCount > 0 || programSetCount > 0);
 const isMobileFilterLayout = useViewFilterDropdown;
 const activeUiSize =
  (typeof document !== 'undefined' && document.body?.dataset?.uiSize) ||
  (typeof window !== 'undefined' ? localStorage.getItem('uiSize') : null) ||
  'medium';
 const isMobileLargeUi = isMobileFilterLayout && activeUiSize === 'large';

 const getFilteredSets = () => {
  const matches = [];

  for (const set of sets) {
    if (currentMode !== 'all' && (set.mode || 'tunes') !== currentMode) continue;
    if (viewFilter === 'programs' && !set.isProgram) continue;
    if (viewFilter === 'sets' && (set.isProgram || set.fromProgram)) continue;
    if (viewFilter === 'program-sets' && (!set.fromProgram || set.isProgram)) continue;
    const q = filterSetQuery.toLowerCase();

let matchedTuneTitles = [];

if (q) {
  (set.tunes || []).forEach(t => {
    if (t.title?.toLowerCase().includes(q)) {
      matchedTuneTitles.push(t.title);
    }
  });
}


    const matchesSearch =
      q === '' ||
      [set.title, set.notes, set.learnedFrom]
        .filter(Boolean)
        .some(field => field.toLowerCase().includes(q)) ||
      (Array.isArray(set.customTags) && set.customTags.some(tag => String(tag || '').toLowerCase().includes(q))) ||
      matchedTuneTitles.length > 0;

    const isNotesCollection = (set.mode || 'tunes') === 'notes';
    const hasItems = Array.isArray(set.tunes) && set.tunes.length > 0;
    const matchesGenre =
      !filterSetGenre
      || (set.genre || '') === filterSetGenre
      || (set.tunes || []).some(t => (t.genre || '') === filterSetGenre)
      || (isNotesCollection && !hasItems);

    const matchesType =
      !filterSetType
      || (set.tunes || []).some(t => normalizeType(t.type) === normalizeType(filterSetType))
      || (isNotesCollection && !hasItems);

    const matchesRating =
      !filterSetRating ||
      (filterSetRating === '—'
        ? !set.rating || set.rating === ''
        : set.rating === filterSetRating);

    const matchesLabel = !filterSetLabel || set.ratingLabel === filterSetLabel;

    const matchesTags =
      selectedSetTags.length === 0 ||
      (setTagLogic === 'AND'
        ? normalizeTagsArray(selectedSetTags).every(tag => normalizeTagsArray(set.customTags || []).includes(tag))
        : normalizeTagsArray(selectedSetTags).some(tag => normalizeTagsArray(set.customTags || []).includes(tag)));

    const matchesSentBy = !filterSentBy || (set.importedFrom?.uid === filterSentBy);
    const matchesTime = matchesSetTimeRange(
      set,
      filterSetTimeRange,
      filterSetTimeBasis,
      filterSetTimeMonth,
      filterSetTimeYear
    );

    if (
      matchesSearch &&
      matchesRating &&
      matchesLabel &&
      matchesGenre &&
      matchesType &&
      matchesTags &&
      matchesSentBy &&
      matchesTime
    ) {
      matches.push({ ...set, _matchedTuneTitles: matchedTuneTitles });
    }
  }

  return matches;
};



const filteredSets = getFilteredSets();
const visibleSetIds = filteredSets.map((s) => s.id);
const allVisibleSetsSelected = visibleSetIds.length > 0 && visibleSetIds.every((id) => selectedSetIds.includes(id));

const clearSetFilters = () => {
  setFilterSetQuery('');
  setFilterSetRating('');
  setFilterSetGenre('');
  setFilterSetType('');
  setFilterSentBy('');
  setSelectedSetTags([]);
  setSetTagLogic('AND');
  setShowSetTagFilters(false);
  setFilterSetTimeRange('');
  setFilterSetTimeBasis('updated');
  setFilterSetTimeMonth('');
  setFilterSetTimeYear('');
  setShowSetTimeFilters(false);
};

const addSetTagFromPreview = (tag) => {
  const normalized = normalizeTag(tag || '');
  if (!normalized) return;
  setShowSetTagFilters(true);
  setSelectedSetTags((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
};

const availableSetTags = useMemo(() => {
  const fromPrefs = Array.isArray(prefs?.customTags) ? prefs.customTags : [];
  const fromSets = sets.flatMap((set) => (Array.isArray(set.customTags) ? set.customTags : []));
  return normalizeTagsArray([...fromPrefs, ...fromSets]).sort((a, b) => a.localeCompare(b));
}, [prefs?.customTags, sets]);

const hasActiveSetFilters = Boolean(
  (filterSetQuery || '').trim() ||
  filterSetRating ||
  filterSetGenre ||
  filterSetType ||
  filterSentBy ||
  filterSetTimeRange ||
  filterSetTimeMonth ||
  filterSetTimeYear ||
  filterSetTimeBasis !== 'updated' ||
  selectedSetTags.length > 0
);
const hasAdvancedSetFiltersActive = Boolean(
  filterSetRating ||
  filterSentBy ||
  !!filterSetTimeRange ||
  filterSetTimeBasis !== 'updated' ||
  (filterSetTimeRange === 'month' && !!filterSetTimeMonth) ||
  (filterSetTimeRange === 'year' && !!filterSetTimeYear) ||
  selectedSetTags.length > 0
);

useEffect(() => {
  if (hasAdvancedSetFiltersActive && !isSimpleMode) setShowAdvancedFilters(true);
}, [hasAdvancedSetFiltersActive, isSimpleMode]);
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { if (isSimpleMode) setShowAdvancedFilters(false); }, [isSimpleMode]);

const ratingCounts = {};
const labelCounts = {};
const sourceCounts = {};

filteredSets.forEach(set => {
  const r = set.rating || '—';
  ratingCounts[r] = (ratingCounts[r] || 0) + 1;

  const l = set.ratingLabel || '—';
  labelCounts[l] = (labelCounts[l] || 0) + 1;


});



// Extract unique genre/type values from current-mode sets only
const allGenres = new Set();
const allTypes = new Set();

// Seed with mode defaults and custom genres so they always appear
(labels?.defaultGenres || []).forEach(g => allGenres.add(g));
(modeGenres || []).forEach(g => allGenres.add(g));
// Seed with mode defaults and custom types so they always appear
(labels?.defaultTypes || []).forEach(t => allTypes.add(normalizeType(t)));
(modeTypes || []).forEach(t => allTypes.add(normalizeType(t)));

sets.filter(s => currentMode === 'all' || (s.mode || 'tunes') === currentMode).forEach(set => {
  if (set.genre) allGenres.add(normalizeGenre(set.genre));
  (set.tunes || []).forEach(t => {
    if (t.genre) allGenres.add(normalizeGenre(t.genre));
    if (t.type) allTypes.add(normalizeType(t.type));
  });
});

const genreOptions = [...allGenres].sort();
const typeOptions = [...allTypes].sort();

const getLastNameSortKey = (name = '') => {
  const normalized = String(name).trim().toLowerCase();
  if (!normalized) return 'zzz';
  const parts = normalized.split(/\s+/).filter(Boolean);
  const last = parts[parts.length - 1] || normalized;
  return `${last} ${normalized}`;
};

const sentByOptions = React.useMemo(() => {
  const map = new Map();
  sets.forEach(s => {
    if (s.importedFrom?.uid) {
      const e = map.get(s.importedFrom.uid);
      if (e) e.count++;
      else map.set(s.importedFrom.uid, { uid: s.importedFrom.uid, name: s.importedFrom.displayName || 'Unknown', count: 1 });
    }
  });
  return Array.from(map.values()).sort((a, b) => getLastNameSortKey(a.name).localeCompare(getLastNameSortKey(b.name)));
}, [sets]);

const getFilteredSubset = (ignoreField) => {
  return sets.filter(set => {
    const q = filterSetQuery.toLowerCase();

    const matchesSearch =
      q === '' ||
      [set.title, set.notes, set.learnedFrom]
        .filter(Boolean)
        .some(field => field.toLowerCase().includes(q)) ||
      (set.tunes || []).some(t => t.title?.toLowerCase().includes(q));

    const matchMap = {
      rating: !filterSetRating || (filterSetRating === '—' ? !set.rating : set.rating === filterSetRating),
      ratingLabel: !filterSetLabel || set.ratingLabel === filterSetLabel,
      genre: !filterSetGenre || normalizeGenre(set.genre) === filterSetGenre || (set.tunes || []).some(t => normalizeGenre(t.genre) === filterSetGenre),
      type: !filterSetType || (set.tunes || []).some(t => normalizeType(t.type) === normalizeType(filterSetType)),
      time: matchesSetTimeRange(
        set,
        filterSetTimeRange,
        filterSetTimeBasis,
        filterSetTimeMonth,
        filterSetTimeYear
      ),
    };

    delete matchMap[ignoreField];
    const restMatch = Object.values(matchMap).every(Boolean);
    return matchesSearch && restMatch;
  });
};

const genreCounts = {};
getFilteredSubset("genre").forEach(set => {
  const genreSet = new Set();
  if (set.genre) genreSet.add(normalizeGenre(set.genre));
  (set.tunes || []).forEach(t => {
    const g = normalizeGenre(t.genre);
    if (g) genreSet.add(g);
  });
  genreSet.forEach(g => {
    genreCounts[g] = (genreCounts[g] || 0) + 1;
  });
});


    const typeCounts = {};
    getFilteredSubset("type").forEach(set => {
      const typeSet = new Set();
      (set.tunes || []).forEach(t => {
        const tNorm = normalizeType(t.type);
        if (tNorm) typeSet.add(tNorm);
      });
      typeSet.forEach(t => {
        typeCounts[t] = (typeCounts[t] || 0) + 1;
      });
    });

const modernGenreOptions = [
  { value: '', label: `All ${labels?.genreLabelPlural || 'Genres'}` },
  ...genreOptions.map((g) => ({ value: g, label: `${g} (${genreCounts[g] || 0})` }))
];
const modernTypeOptions = [
  { value: '', label: 'All Types' },
  ...typeOptions.map((t) => ({ value: t, label: `${t.charAt(0).toUpperCase() + t.slice(1)} (${typeCounts[t] || 0})` }))
];
const modernRatingOptions = [
  { value: '', label: 'All Ratings' },
  { value: '—', label: `No Rating (${ratingCounts['—'] || 0})` },
  ...['★', '★★', '★★★', '★★★★', '★★★★★'].map((r) => ({ value: r, label: `${r} (${ratingCounts[r] || 0})` }))
];
const modernSentByOptions = [
  { value: '', label: 'All Sources' },
  ...sentByOptions.map((o) => ({ value: o.uid, label: `${o.name} (${o.count})` }))
];
const modernSortOptions = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'alpha', label: 'A-Z' },
  { value: 'alphaDesc', label: 'Z-A' }
];
const modernSetBulkActionOptions = [
  {
    label: 'Bulk Actions',
    options: [
      { value: 'action:delete', label: '🗑 Delete Selected' },
      { value: 'action:createset', label: `📁 Create ${labels?.Collection || 'Set'} from Selected` },
      { value: 'action:addtoset', label: '📚 Add to Set' },
      { value: 'action:addtags', label: '🏷 Add Tags' },
      { value: 'action:send', label: '✉ Send to Contact' },
      { value: 'action:export', label: '📥 Export Selected' },
    ],
  },
  {
    label: 'Rating',
    options: [
      { value: 'rating:★', label: '★' },
      { value: 'rating:★★', label: '★★' },
      { value: 'rating:★★★', label: '★★★' },
      { value: 'rating:★★★★', label: '★★★★' },
      { value: 'rating:★★★★★', label: '★★★★★' },
      { value: 'rating:', label: 'Remove Rating' },
    ],
  },
];
const modernViewFilterOptions = [
  { value: 'all', label: 'All Set Types' },
  ...viewFilterOptions.filter((opt) => opt.key !== 'all').map((opt) => ({ value: opt.key, label: opt.label }))
];

  const sortedSets = useMemo(() => {
  const copy = [...filteredSets];

  if (sortOrder === 'alpha') {
    copy.sort((a, b) => a.title.localeCompare(b.title));
  } else if (sortOrder === 'alphaDesc') {
    copy.sort((a, b) => b.title.localeCompare(a.title));
  } else {
    const getTime = (set) => {
      if (!set.createdAt) return 0;
      if (typeof set.createdAt === 'string') return Date.parse(set.createdAt) || 0;
      if (typeof set.createdAt.toMillis === 'function') return set.createdAt.toMillis();
      if (typeof set.createdAt.toDate === 'function') return set.createdAt.toDate().getTime();
      return 0;
    };

    copy.sort((a, b) =>
      sortOrder === 'oldest' ? getTime(a) - getTime(b) : getTime(b) - getTime(a)
    );
  }

  copy.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));

  return copy;
}, [filteredSets, sortOrder]);

const hasActiveSetTimeFilter =
  !!filterSetTimeRange ||
  filterSetTimeBasis !== 'updated' ||
  (filterSetTimeRange === 'month' && !!filterSetTimeMonth) ||
  (filterSetTimeRange === 'year' && !!filterSetTimeYear);
const formatSetMonthLabel = (monthValue) => {
  if (!monthValue) return 'Selected month';
  const [y, m] = String(monthValue).split('-').map(Number);
  if (!y || !m) return 'Selected month';
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};
const setTimeSummaryLabel =
  !filterSetTimeRange && filterSetTimeBasis === 'updated'
    ? 'Time'
    : `Time: ${
      filterSetTimeRange === 'past-week' ? 'Past Week'
        : filterSetTimeRange === 'this-month' ? 'This Month'
          : filterSetTimeRange === 'past-month' ? 'Past Month'
            : filterSetTimeRange === 'past-year' ? 'Past Year'
              : filterSetTimeRange === 'year' ? (filterSetTimeYear || 'Selected year')
                : filterSetTimeRange === 'month' ? formatSetMonthLabel(filterSetTimeMonth)
                  : 'No Time Filter'
    } (${filterSetTimeBasis === 'created' ? 'Created' : filterSetTimeBasis === 'viewed' ? 'Viewed' : 'Updated'})`;
const setTimeRangeOptions = [
  { value: 'past-week', label: 'Past week' },
  { value: 'this-month', label: 'This month' },
  { value: 'past-month', label: 'Past month' },
  { value: 'past-year', label: 'Past year' },
  { value: 'year', label: 'Pick year' },
  { value: 'month', label: 'Pick month' }
];
const setTimeBasisOptions = [
  { value: 'updated', label: 'Updated' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'created', label: 'Created' }
];
const handleSetTimeRangeSelect = (value) => {
  setFilterSetTimeRange(value);
  if (value === 'month') {
    const [y] = (filterSetTimeMonth || '').split('-').map(Number);
    setSetMonthPickerYear(y || new Date().getFullYear());
    setFilterSetTimeYear('');
  } else if (value === 'year') {
    setSetMonthPickerYear(Number(filterSetTimeYear) || new Date().getFullYear());
    setFilterSetTimeMonth('');
  } else {
    setFilterSetTimeMonth('');
    setFilterSetTimeYear('');
  }
};

const handleBulkTagConfirm = async () => {
  if (!user || selectedSetIds.length === 0 || bulkTags.length === 0) return;

  const confirmed = window.confirm(`Add tags to ${selectedSetIds.length} selected ${labels?.collections || 'sets'}?`);
  if (!confirmed) return;

  try {
    for (const id of selectedSetIds) {
      let existing = [];
      let finalTags = [];

      if (navigator.onLine) {
        const ref = doc(db, 'users', user.uid, 'sets', id);
        const snap = await getDoc(ref);
        existing = snap.exists() ? snap.data().customTags || [] : [];
        finalTags = Array.from(new Set([...existing, ...bulkTags]));
        await updateDoc(ref, { customTags: finalTags });
      } else {
        const local = await dexie.sets.get(id);
        existing = local?.customTags || [];
        finalTags = Array.from(new Set([...existing, ...bulkTags]));
        await dexie.sets.update(id, { customTags: finalTags });

        await dexie.pendingWrites.put({
          id: `${id}__bulkTag`,
          type: 'update',
          collection: 'sets',
          docId: id,
          data: { customTags: finalTags }
        });
      }
    }

    setShowBulkTagInput(false);
    setBulkTags([]);
    setSelectedSetIds([]);
    showNotice(`Tags added to ${selectedSetIds.length} ${labels?.collections || 'sets'}.`);
  } catch (err) {
    console.error('Failed to update tags on sets:', err);
    showNotice('Failed to apply tags.', 'error');
  }
};

async function handleCreateSetFromSelectedSets() {
  if (!user || selectedSetIds.length === 0) return;
  const orderedSets = selectedSetIds
    .map((id) => sets.find((s) => s.id === id))
    .filter(Boolean)
    .map((set) => ({ id: set.id, title: set.title || 'Untitled Set', type: 'set' }));
  if (orderedSets.length === 0) return;

  const selectedMode = sets.find((s) => s.id === selectedSetIds[0])?.mode || (currentMode === 'all' ? 'tunes' : currentMode);
  const createdAt = new Date().toISOString();
  const payload = {
    title: orderedSets[0]?.title || `Untitled ${labels?.Collection || 'Set'}`,
    mode: selectedMode,
    tunes: orderedSets,
    notes: '',
    rating: '',
    customTags: [],
    createdAt,
    updatedAt: createdAt,
  };

  try {
    let newSetId = '';
    newSetId = crypto.randomUUID();
    if (navigator.onLine) {
      await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), { id: newSetId, ...payload });
      await dexie.sets.put({ id: newSetId, ...payload });
    } else {
      await dexie.sets.put({ id: newSetId, ...payload });
      await dexie.pendingWrites.put({
        id: newSetId,
        type: 'set',
        operation: 'add',
        data: { id: newSetId, ...payload }
      });
    }

    setSelectedSetIds([]);
    navigate(`/sets/${newSetId}`);
  } catch (err) {
    console.error('Failed to create set from selected sets:', err);
    showNotice('Failed to create set.', 'error');
  }
}

const bulkAddTargetSets = useMemo(() => {
  const selectedIds = new Set(selectedSetIds);
  const q = (bulkAddToSetQuery || '').trim().toLowerCase();
  const selectedMode = sets.find((s) => s.id === selectedSetIds[0])?.mode || currentMode;
  return sets
    .filter((set) => !set.isProgram)
    .filter((set) => !selectedIds.has(set.id))
    .filter((set) => currentMode === 'all' ? ((set.mode || 'tunes') === selectedMode) : (set.mode || 'tunes') === currentMode)
    .filter((set) => !q || (set.title || '').toLowerCase().includes(q))
    .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
}, [sets, selectedSetIds, currentMode, bulkAddToSetQuery]);

async function handleBulkAddToSet(targetSetId) {
  if (!user || !targetSetId || selectedSetIds.length === 0) return;
  const selectedMap = new Map(sets.map((s) => [s.id, s]));
  const targetSet = selectedMap.get(targetSetId);
  if (!targetSet) return;

  const setEntriesToAdd = selectedSetIds
    .filter((id) => id !== targetSetId)
    .map((id) => selectedMap.get(id))
    .filter(Boolean)
    .map((set) => ({ id: set.id, title: set.title || 'Untitled Set', type: 'set' }));

  if (setEntriesToAdd.length === 0) {
    showNotice('No valid sets to add.', 'info');
    return;
  }

  const existing = Array.isArray(targetSet.tunes) ? targetSet.tunes : [];
  const existingSetIds = new Set(
    existing
      .filter((entry) => entry?.type === 'set' && entry?.id)
      .map((entry) => entry.id)
  );
  const uniqueToAdd = setEntriesToAdd.filter((entry) => !existingSetIds.has(entry.id));

  if (uniqueToAdd.length === 0) {
    showNotice('All selected sets are already in that set.', 'info');
    return;
  }

  const nextTunes = [...existing, ...uniqueToAdd];
  const updatedAt = new Date().toISOString();

  try {
    if (navigator.onLine) {
      await updateDoc(doc(db, 'users', user.uid, 'sets', targetSetId), {
        tunes: nextTunes,
        updatedAt
      });
    } else {
      await dexie.sets.update(targetSetId, { tunes: nextTunes, updatedAt });
      await dexie.pendingWrites.put({
        id: `${targetSetId}__bulkAddToSet`,
        type: 'update',
        collection: 'sets',
        docId: targetSetId,
        data: { tunes: nextTunes, updatedAt }
      });
    }

    setSets((prev) => prev.map((set) => (
      set.id === targetSetId ? { ...set, tunes: nextTunes, updatedAt } : set
    )));
    setSelectedSetIds([]);
    setShowBulkAddToSetPicker(false);
    setBulkAddToSetQuery('');
    showNotice(`Added ${uniqueToAdd.length} set${uniqueToAdd.length !== 1 ? 's' : ''} to "${targetSet.title || 'Untitled Set'}".`, 'info');
  } catch (err) {
    console.error('Failed to add sets to target set:', err);
    showNotice('Failed to add to set.', 'error');
  }
}

async function handleBulkSendSets(recipientUid, contact) {
  const myProfile = await getUserProfile(user.uid);
  const items = selectedSetIds.map(id => {
    const s = sets.find(s => s.id === id);
    const tuneIds = (s?.tunes || []).filter(t => t.type !== 'set').map(t => t.id);
    return { itemType: 'set', setId: id, setTitle: s?.title || 'Untitled Set', tuneIds };
  });
  await sendBatchToUser(user.uid, myProfile, recipientUid, items);

  // Record sharedWith on each set
  for (const id of selectedSetIds) {
    const ref = doc(db, 'users', user.uid, 'sets', id);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const existing = snap.data().sharedWith || [];
      if (!existing.some(s => s.uid === recipientUid)) {
        await updateDoc(ref, {
          sharedWith: [...existing, {
            uid: recipientUid,
            displayName: contact?.displayName || '',
            username: contact?.username || '',
            sentAt: new Date().toISOString()
          }]
        });
      }
    }
  }
  setSelectedSetIds([]);
}


  return (
    <PageWrapper>
{notice && (
  <div style={{
    margin: '0.5rem auto 0.75rem',
    maxWidth: '680px',
    padding: '8px 12px',
    borderRadius: '8px',
    textAlign: 'center',
    backgroundColor: notice.tone === 'error' ? '#5a1f1f' : '#1f3f5a',
    color: '#fff',
    fontSize: '0.9rem'
  }}>
    {notice.text}
  </div>
)}

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>


<BorderedBox
  collapsible
  scrollWhenExpanded
  expandedMaxHeight="50vh"
  style={{ marginBottom: 'var(--ui-section-gap)', paddingTop: 'var(--ui-panel-padding-top)' }}
  exposeCollapsed={() => setTimeout(updateScrollHeight, 200)}
>

  <div className="app-title ui-density-title" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
    <img src={myIcon} alt="Logo" className="responsive-icon" />
    <span>{labels.navSets}</span>
    <FaQuestionCircle
      onClick={() => navigate(`/help#${currentMode === 'notes' ? 'folders' : 'sets'}`)}
      style={{ fontSize: '0.65em', cursor: 'pointer', color: 'var(--text-link, #228be6)', opacity: 0.8 }}
      title="Help & Tutorials"
    />
  </div>
  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
    <ModeSwitcher inline />
  </div>

  {NudgeBanner && <div style={{ maxWidth: '600px', margin: '0 auto 10px' }}><NudgeBanner /></div>}

 <div style={{ maxWidth: '600px', margin: '0 auto' }}>

<div
  className="ui-density-grid search-filter-grid"
  style={{
    display: 'grid',
    gridTemplateColumns: isMobileFilterLayout ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(160px, 1fr))',
    maxWidth: '600px',
    alignItems: 'center',
  }}
>
      <input
        className="search-input"
        placeholder="Search title or any metadata..."
        value={filterSetQuery}
        onChange={(e) => setFilterSetQuery(e.target.value)}
        style={{ ...inputStyle, gridColumn: '1 / -1' }}
      />

      <div
        style={{
          gridColumn: '1 / -1',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 'var(--ui-filter-grid-gap)',
          alignItems: 'center'
        }}
      >
        <AppSelect
          className="search-core-select"
          value={filterSetGenre || ''}
          onChange={setFilterSetGenre}
          options={modernGenreOptions}
          placeholder={`All ${labels?.genreLabelPlural || 'Genres'}`}
          active={Boolean(filterSetGenre)}
        />

        <AppSelect
          className="search-core-select"
          value={filterSetType || ''}
          onChange={setFilterSetType}
          options={modernTypeOptions}
          placeholder="All Types"
          active={Boolean(filterSetType)}
        />

        <AppSelect
          className="search-core-select"
          value={viewFilter || 'all'}
          onChange={setViewFilter}
          options={modernViewFilterOptions}
          placeholder="All Set Types"
          active={Boolean(viewFilter && viewFilter !== 'all')}
        />
      </div>

  <div
    className="quick-filter-actions search-utility-row"
    style={{
      gridColumn: '1 / -1',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: 'var(--ui-filter-grid-gap)',
      alignItems: 'center'
    }}
  >
    <button
      className={`btn-subtle search-control-btn search-more-btn ${hasAdvancedSetFiltersActive ? 'search-control-active' : ''} ${showAdvancedFilters ? 'control-ready-glow' : ''}`}
      style={isSimpleMode ? { opacity: 0.5 } : undefined}
      onClick={() => {
        if (isSimpleMode) { setShowAdvancedLockPopup(true); return; }
        setShowAdvancedFilters((prev) => {
          const next = !prev;
          if (!next) {
            setShowSetTagFilters(false);
            setShowSetTimeFilters(false);
          }
          return next;
        });
      }}
    >
      {isSimpleMode
        ? (isMobileFilterLayout ? '↓ More 🔒' : '↓ More Filters 🔒')
        : showAdvancedFilters
          ? (isMobileFilterLayout ? '↑ Hide' : '↑ Hide Advanced')
          : (isMobileFilterLayout
            ? `↓ More${hasAdvancedSetFiltersActive ? ' • Active' : ''}`
            : `↓ More Filters${hasAdvancedSetFiltersActive ? ' • Active' : ''}`)}
    </button>

    <button
      className="btn-subtle btn-clear-filters search-control-btn search-clear-btn"
      onClick={clearSetFilters}
      disabled={!hasActiveSetFilters}
    >
      {isMobileFilterLayout ? 'Clear' : '✕ Clear Filters'}
    </button>

    <button
      title="Jump to a random set from the current filter"
      onClick={() => {
        const matching = getFilteredSets();
        if (matching.length === 0) return;
        const random = matching[Math.floor(Math.random() * matching.length)];
        navigate(`/sets/${random.id}`, {
          state: {
            setTunes: matching.map((s) => ({ id: s.id, title: s.title, type: 'set' })),
          },
        });
      }}
      className="btn-subtle search-control-btn search-random-btn"
    >
      🎲 {labels?.randomCollection || 'Random Set'}
    </button>
  </div>

  {showAdvancedFilters && !isSimpleMode && (
    <>
      {sentByOptions.length > 0 && (
        <AppSelect
          className="search-advanced-select"
          value={filterSentBy || ''}
          onChange={setFilterSentBy}
          options={modernSentByOptions}
          placeholder="All Sources"
          active={Boolean(filterSentBy)}
        />
      )}

      <div
        className="quick-filter-actions search-advanced-row"
        style={{
          gridColumn: '1 / -1',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 'var(--ui-filter-grid-gap)',
          alignItems: 'center'
        }}
      >
        <button
          className={`btn-subtle search-control-btn search-time-btn ${hasActiveSetTimeFilter ? 'search-control-active' : ''} ${showSetTimeFilters ? 'panel-open' : ''}`}
          onClick={() => setShowSetTimeFilters(v => !v)}
        >
          {showSetTimeFilters ? '🕒 Hide Time' : `🕒 ${setTimeSummaryLabel}`}
        </button>
        <button
          className={`btn-subtle search-control-btn search-advanced-btn ${selectedSetTags.length > 0 ? 'search-control-active' : ''} ${showSetTagFilters ? 'panel-open' : ''}`}
          onClick={() => setShowSetTagFilters(v => !v)}
        >
          {showSetTagFilters
            ? `🏷 Hide Tags${selectedSetTags.length > 0 ? ` (${selectedSetTags.length})` : ''}`
            : `🏷 Tags${selectedSetTags.length > 0 ? ` (${selectedSetTags.length})` : ''}`}
        </button>

        <AppSelect
          className="search-advanced-select"
          value={filterSetRating || ''}
          onChange={setFilterSetRating}
          options={modernRatingOptions}
          placeholder="All Ratings"
          active={Boolean(filterSetRating)}
        />
      </div>
    </>
  )}

{showSetTimeFilters && (
  <TimeFilterPanel
    isMobileFilterLayout={isMobileFilterLayout}
    uiSize={activeUiSize}
    basisOptions={setTimeBasisOptions}
    selectedBasis={filterSetTimeBasis}
    onSelectBasis={setFilterSetTimeBasis}
    rangeOptions={setTimeRangeOptions}
    selectedRange={filterSetTimeRange}
    onSelectRange={handleSetTimeRangeSelect}
    hasActiveTimeFilter={hasActiveSetTimeFilter}
    onClear={() => {
      setFilterSetTimeRange('');
      setFilterSetTimeBasis('updated');
      setFilterSetTimeMonth('');
      setFilterSetTimeYear('');
    }}
    monthPickerYear={setMonthPickerYear}
    setMonthPickerYear={setSetMonthPickerYear}
    selectedMonth={filterSetTimeMonth}
    onSelectMonth={setFilterSetTimeMonth}
    selectedYear={filterSetTimeYear}
    onSelectYear={setFilterSetTimeYear}
  />
)}

{showSetTagFilters && availableSetTags.length > 0 && (
  <div style={{ gridColumn: '1 / -1' }}>
    <TagFilterControl
      availableTags={availableSetTags}
      selectedTags={selectedSetTags}
      setSelectedTags={setSelectedSetTags}
      tagLogic={setTagLogic}
      setTagLogic={setSetTagLogic}
    />
  </div>
)}

<div
  className="search-actions-wrap"
  style={{ gridColumn: '1 / -1', width: '100%', maxWidth: '600px', margin: '0 auto' }}
>
  <div className="tune-toolbar">
    <div className="tune-toolbar-row search-actions-row">
      <AppSelect
        className="compact-action-select search-sort-select"
        value={sortOrder}
        onChange={setSortOrder}
        options={modernSortOptions}
        placeholder="Sort"
        active={Boolean(sortOrder && sortOrder !== 'newest')}
      />

      <button
        className={`btn-subtle compact-action-btn search-control-btn search-selectall-btn ${selectedSetIds.length > 0 ? 'search-control-active' : ''}`}
        onClick={toggleSelectAll}
      >
        {allVisibleSetsSelected ? 'Deselect All' : 'Select All'}
      </button>

      <AppSelect
        className={`bulk-actions-select search-bulk-select ${selectedSetIds.length > 0 ? 'bulk-ready control-ready-glow' : ''}`}
        value={bulkActionValue}
        onChange={async (value) => {
  if (!value || selectedSetIds.length === 0) return;

  if (value === 'action:delete') {
    setShowDeleteSelectedModal(true);
    setBulkActionValue('');
    return;
  }

  else if (value === 'action:createset') {
    setShowCreateSetFromSelectedModal(true);
    setBulkActionValue('');
    return;
  }

  else if (value === 'action:addtags') {
    setShowBulkTagInput(true);
    return;
  }

  else if (value === 'action:addtoset') {
    setShowBulkAddToSetPicker(true);
    return;
  }

  else if (value === 'action:send') {
    setShowBulkSendPicker(true);
    return;
  }

  else if (value === 'action:export') {
    setShowBulkExportModal(true);
    return;
  }

  else if (value.startsWith('rating:')) {
    const newRating = value.split(':')[1];
    const confirmText = newRating
      ? `Set rating to "${newRating}"`
      : `Remove rating`;

    const confirmed = window.confirm(
      `${confirmText} for ${selectedSetIds.length} set${selectedSetIds.length > 1 ? 's' : ''}?`
    );
    if (!confirmed) return;

    for (const id of selectedSetIds) {
      const ref = doc(db, 'users', user.uid, 'sets', id);
      await updateDoc(ref, { rating: newRating || null });
    }

    setSelectedSetIds([]);
  }

  // ✅ Only clear selection after handling
  setBulkActionValue('');
}}
        placeholder={
          selectedSetIds.length > 0
            ? `Bulk Actions (${selectedSetIds.length} selected)`
            : 'Bulk Actions'
        }
        options={modernSetBulkActionOptions}
        active={selectedSetIds.length > 0}
      />
    </div>
  </div>
</div>

</div>

{showBulkTagInput && (
  <div
    style={{
      marginTop: '0.5rem',
      padding: '0.5rem',
      borderTop: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
      Add tags to selected {labels?.collections || 'sets'}:
    </p>

    <div
      style={{
        width: '100%',
        maxWidth: '400px',
        boxSizing: 'border-box',
        padding: '0 1rem',
      }}
    >
      <CustomTagInput
        tags={bulkTags}
        setTags={setBulkTags}
        suggestions={availableSetTags}
        inputStyle={inputStyle}
      />
    </div>

    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem' }}>
      <button
        onClick={handleBulkTagConfirm}
        style={{
          padding: '6px 12px',
          fontSize: '0.8rem',
          borderRadius: '6px',
          backgroundColor: '#228be6',
          color: 'white'
        }}
      >
        Apply Tags
      </button>

      <button
        onClick={() => {
          setShowBulkTagInput(false);
          setBulkTags([]);
        }}
        style={{ fontSize: '0.8rem' }}
      >
        Cancel
      </button>
    </div>
  </div>
)}
<div style={{ display: 'flex', justifyContent: 'center', gap: '0.45rem', marginTop: '1rem', flexWrap: 'wrap' }}>
  {currentMode === 'all' ? (
    <>
      <button
        onClick={async () => {
          if (!user) return;
          const newSetId = crypto.randomUUID();
          await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), {
            id: newSetId, title: '', notes: '', rating: '', ratingLabel: '',
            learnedFrom: '', tunes: [], mode: 'tunes', createdAt: new Date().toISOString()
          });
          navigate(`/sets/${newSetId}`);
        }}
        className="btn-subtle search-control-btn"
        style={{
          whiteSpace: 'nowrap',
          minHeight: 'var(--ui-select-height, 28px)',
          backgroundColor: 'color-mix(in srgb, var(--input-bg) 72%, #228be6 28%)',
          border: '1px solid color-mix(in srgb, #228be6 52%, var(--card-border) 48%)'
        }}
      >
        + New Set
      </button>
      <button
        onClick={async () => {
          if (!user) return;
          const newSetId = crypto.randomUUID();
          await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), {
            id: newSetId, title: '', notes: '', rating: '', ratingLabel: '',
            learnedFrom: '', tunes: [], mode: 'notes', createdAt: new Date().toISOString()
          });
          navigate(`/sets/${newSetId}`);
        }}
        className="btn-subtle search-control-btn"
        style={{
          whiteSpace: 'nowrap',
          minHeight: 'var(--ui-select-height, 28px)',
          backgroundColor: 'color-mix(in srgb, var(--input-bg) 72%, #f76707 28%)',
          border: '1px solid color-mix(in srgb, #f76707 52%, var(--card-border) 48%)'
        }}
      >
        + New Folder
      </button>
    </>
  ) : (
    <button
      onClick={async () => {
        if (!user) return;
        const newSetId = crypto.randomUUID();
        await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), {
          id: newSetId, title: '', notes: '', rating: '', ratingLabel: '',
          learnedFrom: '', tunes: [], mode: currentMode, createdAt: new Date().toISOString()
        });
        navigate(`/sets/${newSetId}`);
      }}
      className="btn-subtle search-control-btn"
      style={{
        whiteSpace: 'nowrap',
        minHeight: 'var(--ui-select-height, 28px)',
        backgroundColor: 'color-mix(in srgb, var(--input-bg) 72%, #228be6 28%)',
        border: '1px solid color-mix(in srgb, #228be6 52%, var(--card-border) 48%)'
      }}
    >
      {labels?.newCollection || '+ New Set'}
    </button>
  )}

  {currentMode !== 'notes' && (
  <button
    onClick={async () => {
      if (isSimpleMode) { setShowAdvancedLockPopup(true); return; }
      if (!user) return;
      const newSetId = crypto.randomUUID();
      await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), {
        id: newSetId, title: '', notes: '', rating: '', isProgram: true,
        slots: [], tunes: [], mode: currentMode === 'all' ? 'tunes' : currentMode,
        createdAt: new Date().toISOString()
      });
      navigate(`/sets/${newSetId}/edit-program`);
    }}
    className="btn-subtle search-control-btn"
    style={{
      whiteSpace: 'nowrap',
      minHeight: 'var(--ui-select-height, 28px)',
      backgroundColor: 'color-mix(in srgb, var(--input-bg) 72%, #9c36b5 28%)',
      border: '1px solid color-mix(in srgb, #9c36b5 52%, var(--card-border) 48%)',
      ...(isSimpleMode ? { opacity: 0.45 } : {}),
    }}
  >
    + New Program{isSimpleMode ? ' 🔒' : ''}
  </button>
  )}

  {!labels?.hideSearchTools && (
  <button
    onClick={() => setShowIrishForm(v => !v)}
    className="btn-subtle search-control-btn search-random-btn"
    style={{
      whiteSpace: 'nowrap',
      minHeight: 'var(--ui-select-height, 28px)',
      backgroundColor: showIrishForm ? undefined : 'color-mix(in srgb, var(--input-bg) 72%, #2f9e44 28%)',
      border: showIrishForm ? undefined : '1px solid color-mix(in srgb, #2f9e44 52%, var(--card-border) 48%)'
    }}
  >
    {showIrishForm ? '✖ Cancel' : '🎻 Irish Set Generator'}
  </button>
  )}
</div>

{!labels?.hideSearchTools && (
<CreateIrishSetForm
  user={user}
  inputStyle={inputStyle}
  tunes={tunes}
  showForm={showIrishForm}
  setShowForm={setShowIrishForm}
/>
)}



    </div>


 
</BorderedBox>

{prefs?.showFieldHints && (
  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', margin: '0.5rem auto', maxWidth: '500px' }}>
    Search and filter your {labels.collections} by title, genre, type, rating, or tags.
    <div style={{ fontSize: '0.7rem', marginTop: '0.25rem', opacity: 0.7 }}>
      <span onClick={() => navigate('/help')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Toggle hints in Help &amp; Tutorials</span>
    </div>
  </div>
)}

</div>



{sets.length === 0 ? (
  <div style={{ textAlign: 'center', padding: '2rem 1rem', lineHeight: '1.8' }}>
    <p style={{ fontSize: '1.1em', marginBottom: '0.5rem' }}>No {labels.collections} yet</p>
    <p>{labels.Collections} let you group {labels.items} together — {labels.collectionDescription}.</p>
    <p style={{ fontSize: '0.9em', opacity: 0.8 }}>Create one using the <strong>{labels.newCollection}</strong> button above, or select multiple {labels.items} on the Search page and use <strong>Create {labels.Collection}</strong>.</p>
    <p style={{ fontSize: '0.9em', opacity: 0.8 }}>Check out <a href="/help" style={{ color: 'var(--text-primary)' }}>Help &amp; Tutorials</a> for more tips.</p>
  </div>
) : (
<div
  ref={resultsContainerRef}
  style={{
    overflowY: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    marginBottom: '2rem',
    boxSizing: 'border-box',
    height: scrollAreaHeight,
  }}
>

{pendingDelete && (
  <UndoBanner
    className="undo-banner-inline"
    message={`Deleted ${pendingDelete.length} ${pendingDelete.length === 1
      ? deletedSetUndoLabel.singular
      : deletedSetUndoLabel.plural}.`}
    onUndo={undoDelete}
  />
)}
{filterSentBy && (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px', marginBottom: '0.5rem', padding: '6px 12px',
    backgroundColor: 'var(--input-bg)', borderRadius: '8px', fontSize: '0.85rem'
  }}>
    <span>Showing sets from <strong>{sentByOptions.find(o => o.uid === filterSentBy)?.name || 'contact'}</strong></span>
    <button className="btn-subtle" onClick={() => { setFilterSentBy(''); navigate('/sets'); }} style={{ padding: '2px 8px', fontSize: '0.8rem' }}>
      Clear
    </button>
  </div>
)}
    {sortedSets.length === 0 && (
      <div style={{ textAlign: 'center', padding: '1.5rem 1rem', lineHeight: '1.8' }}>
        <p style={{ fontSize: '1.05em', marginBottom: '0.35rem' }}>No {labels.collections} match your filters.</p>
        <p style={{ fontSize: '0.9em', opacity: 0.8, marginBottom: '0.75rem' }}>
          Try clearing one filter or changing your sort/filter combination.
        </p>
        <button
          className="btn-subtle btn-clear-filters"
          onClick={clearSetFilters}
          disabled={!hasActiveSetFilters}
        >
          Clear Filters
        </button>
      </div>
    )}
    {sortedSets.length > 0 && (
    <ul className="card-list">
      {sortedSets.map(set => {
        const setTags = Array.isArray(set.customTags) ? set.customTags : [];
        const previewTags = selectedSetTags.length > 0
          ? setTags.filter(tag => selectedSetTags.includes(tag))
          : setTags;
        const showTagPreview = prefs?.showTagPreviewInSets !== false;
        const showSetDatePreview = prefs?.showSetDateInSets !== false;
        const setDate = toDateValue(set.createdAt) || toDateValue(set.updatedAt);
        const setDateLabel = showSetDatePreview && setDate ? formatShortUsDate(setDate) : '';
        return (
        <SwipeablePinnableRow
          key={set.id}
          pinned={set.pinned}
          actions={[
            { icon: '📤', label: 'Send / Export', color: '#1c7ed6', onAction: () => setSwipeSendTarget(set) },
            { icon: '🗑', label: 'Delete', color: '#e03131', onAction: () => swipeDeleteSet(set) },
            { icon: set.pinned ? '📌' : '📍', label: set.pinned ? 'Unpin' : 'Pin', color: '#e07622', onAction: () => swipePinSet(set) },
          ]}
        >
        <TuneCardBox
          onClick={() => {
            const navigationState = buildSetSequenceState();
              if (activeSession && !set.isProgram) {
              setPendingSessionSet({ id: set.id, title: (set.title || '').trim() || 'Untitled', navigationState });
              return;
            }
            navigate(`/sets/${set.id}`, { state: navigationState });
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: isMobileFilterLayout ? 'wrap' : 'nowrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flex: '1 1 0',
                minWidth: 0,
              }}
            >
            <div
              onClick={(e) => {
                e.stopPropagation();
                toggleSetSelection(set.id);
              }}
              style={{
                padding: '6px',
                margin: '-6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '40px',
                minHeight: '40px',
                touchAction: 'manipulation',
              }}
            >
              <StyledCheckbox
                checked={selectedSetIds.includes(set.id)}
                onChange={() => {}}
              />
            </div>


              <div>
                <div
                  className="preview-title-row"
                >
                  <strong style={{ fontSize: '1rem' }}>{(set.title || '').trim() || 'Untitled'}</strong>
                  {set.isProgram && (
                    <PreviewModePill variant="program-template">PROGRAM TEMPLATE</PreviewModePill>
                  )}
                  {!set.isProgram && set.fromProgram && (
                    <PreviewModePill variant="program">PROGRAM</PreviewModePill>
                  )}
                  {!set.isProgram && !set.fromProgram && (
                    <PreviewModePill variant={getCollectionBadge(set.mode || 'tunes') === 'FOLDER' ? 'folder' : 'set'}>
                      {getCollectionBadge(set.mode || 'tunes')}
                    </PreviewModePill>
                  )}
                  <span style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                    — {set.isProgram
                      ? `${set.slots?.length || 0} ${(set.slots?.length || 0) !== 1 ? 'slots' : 'slot'}`
                      : `${set.tunes?.length || 0} ${(set.tunes?.length || 0) !== 1 ? (labels?.items || 'tunes') : (labels?.item || 'tune')}`
                    }
                  </span>
                </div>

                <div
                  style={{
                    fontSize: '0.9rem',
                    color: 'var(--text-muted)',
                    marginTop: '2px',
                  }}
                >
                  {setDateLabel && (
                    <span style={{ marginRight: '10px', color: 'var(--text-muted)' }}>
                      {setDateLabel}
                    </span>
                  )}
                  {[set.rating, set.learnedFrom]
                    .filter(Boolean)
                    .map((val, i) => (
                      <span
                        key={i}
                        style={{
                          marginRight: '10px',
                          color: i === 0 ? '#cc9900' : i === 1 ? '#666' : '#444',
                          fontStyle: i === 1 ? 'italic' : 'normal',
                        }}
                      >
                        {i === 2 ? `[from ${val}]` : val}
                      </span>
                    ))}
                </div>

{set.isProgram ? (
  set.slots?.length > 0 && (
    <div className="text-scaled" style={{ marginTop: '4px' }}>
      {set.slots.map((s, i) => (
        <span key={i}>
          {s.name}
          {i < set.slots.length - 1 && ', '}
        </span>
      ))}
    </div>
  )
) : (
  set.tunes?.length > 0 && (
    <div className="text-scaled" style={{ marginTop: '4px', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
      {set.tunes.map((t, i) => (
        <span key={i}>
          {set._matchedTuneTitles?.length > 0 && set._matchedTuneTitles.includes(t.title) ? (
            <strong>{t.title}</strong>
          ) : (
            t.title
          )}
          {i < set.tunes.length - 1 && ', '}
        </span>
      ))}
    </div>
  )
)}

              </div>
            </div>

            {showTagPreview && previewTags.length > 0 && (
              <div
                style={{
                  flex: isMobileFilterLayout ? '1 1 100%' : '0 0 auto',
                  width: isMobileFilterLayout ? 'calc(100% - 52px)' : 'auto',
                  marginLeft: isMobileFilterLayout ? '52px' : 'auto',
                  alignSelf: 'center',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: isMobileFilterLayout ? 'flex-start' : 'flex-end',
                  gap: '4px',
                  maxWidth: isMobileFilterLayout ? '100%' : '180px',
                }}
              >
                {previewTags.slice(0, 6).map((tag, idx) => {
                  const normalizedTag = normalizeTag(tag || '');
                  const normalizedQuery = normalizeTag((filterSetQuery || '').trim());
                  const active = normalizeTagsArray(selectedSetTags).includes(normalizedTag) || (
                    Boolean(normalizedQuery) && normalizedTag.includes(normalizedQuery)
                  );
                  return (
                  <button
                    key={`${set.id}-matched-tag-${idx}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      addSetTagFromPreview(tag);
                    }}
                    className="btn-subtle"
                    style={{
                      fontSize: 'var(--ui-card-chip-font)',
                      padding: '1px 6px',
                      borderRadius: '10px',
                      lineHeight: 1.15,
                      minHeight: '20px',
                      whiteSpace: 'nowrap',
                      textDecoration: 'underline',
                      backgroundColor: active
                        ? 'color-mix(in srgb, var(--text-link, #228be6) 22%, transparent)'
                        : 'color-mix(in srgb, var(--text-muted) 16%, transparent)',
                      border: active
                        ? '1px solid color-mix(in srgb, var(--text-link, #228be6) 58%, transparent)'
                        : '1px solid transparent',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {tag}
                  </button>
                );})}
              </div>
            )}
          </div>
        </TuneCardBox>
        </SwipeablePinnableRow>
      );})}
    </ul>
    )}
  </div>
)}

      {pendingSessionSet && (
        <SessionPromptModal
          message={`Tag "${pendingSessionSet.title}" to the active session?`}
          onConfirm={async () => {
            try {
              const taggedCount = await tagSetToActiveSession(pendingSessionSet.id);
              if (taggedCount > 0) {
                showNotice('Set tagged to active session.', 'info');
              } else {
                showNotice('This set is already tagged to the active session.', 'info');
              }
            } catch (err) {
              console.error('Failed to add set to active session', err);
              showNotice('Could not tag set to active session.', 'error');
            } finally {
              setPendingSessionSet(null);
            }
          }}
          onCancel={() => {
            navigate(`/sets/${pendingSessionSet.id}`, { state: pendingSessionSet.navigationState });
            setPendingSessionSet(null);
          }}
        />
      )}

      <ActionConfirmModal
        open={showAdvancedLockPopup}
        title="Advanced Filters"
        message="Advanced filters let you narrow results by rating, tags, time range, and more. Full Mode also unlocks Sessions (playing history), Social (follow musicians, share tunes), and program templates."
        confirmLabel="Enable Full Mode"
        cancelLabel="Not yet"
        onConfirm={() => { setShowAdvancedLockPopup(false); unlockFullMode(); }}
        onCancel={() => setShowAdvancedLockPopup(false)}
      />

      <ActionConfirmModal
        open={showDeleteSelectedModal}
        title={`Delete ${selectedSetIds.length === 1 ? selectedSetDeleteLabel.singular : selectedSetDeleteLabel.plural}`}
        message={`Delete ${selectedSetIds.length} selected ${selectedSetIds.length === 1
          ? selectedSetDeleteLabel.singular
          : selectedSetDeleteLabel.plural}?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={async () => {
          setShowDeleteSelectedModal(false);
          await deleteSelectedSets();
        }}
        onCancel={() => setShowDeleteSelectedModal(false)}
      />

      <ActionConfirmModal
        open={showCreateSetFromSelectedModal}
        title={`Create ${labels?.Collection || 'Set'}`}
        message={`Create new ${labels?.collection || 'set'} with ${selectedSetIds.length} selected set${selectedSetIds.length !== 1 ? 's' : ''}?`}
        confirmLabel="Create"
        cancelLabel="Cancel"
        onConfirm={async () => {
          setShowCreateSetFromSelectedModal(false);
          await handleCreateSetFromSelectedSets();
        }}
        onCancel={() => setShowCreateSetFromSelectedModal(false)}
      />

      <SendExportModal
        open={!!swipeSendTarget}
        onClose={() => setSwipeSendTarget(null)}
        title={swipeSendTarget?.title}
        user={user}
        onSend={handleSwipeSendSet}
        type="set"
        item={swipeSendTarget}
        tunes={tunes}
      />

      <SendExportModal
        open={showBulkSendPicker}
        onClose={() => setShowBulkSendPicker(false)}
        title={`${selectedSetIds.length} set${selectedSetIds.length !== 1 ? 's' : ''}`}
        user={user}
        onSend={handleBulkSendSets}
        type="bulk"
        item={null}
        tunes={sets.filter(s => selectedSetIds.includes(s.id))}
        defaultTab="send"
      />

      <ExportModal
        open={showBulkExportModal}
        onClose={() => setShowBulkExportModal(false)}
        type="bulk"
        item={null}
        tunes={sets.filter(s => selectedSetIds.includes(s.id))}
      />

      {/* Bulk Add-To-Set Picker */}
      {showBulkAddToSetPicker && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
        }} onClick={() => { setShowBulkAddToSetPicker(false); setBulkAddToSetQuery(''); }}>
          <div
            style={{
              backgroundColor: 'var(--bg-primary)', borderRadius: '12px', padding: '1.5rem',
              width: '100%', maxWidth: '420px', maxHeight: '72vh', overflowY: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0 }}>Add {selectedSetIds.length} set{selectedSetIds.length !== 1 ? 's' : ''} to...</h3>
              <button className="btn-subtle search-control-btn" onClick={() => { setShowBulkAddToSetPicker(false); setBulkAddToSetQuery(''); }}
                style={{ padding: '4px 10px', fontSize: '1rem' }}>✕</button>
            </div>
            <input
              className="search-input"
              placeholder="Search target sets..."
              value={bulkAddToSetQuery}
              onChange={(e) => setBulkAddToSetQuery(e.target.value)}
              style={{ ...inputStyle, width: '100%', marginBottom: '0.75rem' }}
            />

            {bulkAddTargetSets.length === 0 ? (
              <p style={{ textAlign: 'center', opacity: 0.75 }}>No eligible target sets found.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {bulkAddTargetSets.map((target) => (
                  <button
                    key={target.id}
                    className="btn-subtle search-control-btn"
                    onClick={() => handleBulkAddToSet(target.id)}
                    style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '8px 12px' }}
                  >
                    {target.title || 'Untitled Set'}
                    <span style={{ opacity: 0.7, marginLeft: '6px', fontSize: '0.85em' }}>
                      ({Array.isArray(target.tunes) ? target.tunes.length : 0} items)
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </PageWrapper>
  );
}
